import React from 'react';
import { Box, Paper, Typography, Avatar, Divider, Chip, useTheme, Grid } from '@mui/material';
import { Person, Email, Phone, Badge, CalendarToday, Verified } from '@mui/icons-material';
import { PageHeader } from '@shared/components/PageHeader';
import { getStoredUserData } from '@core/utils/auth';

const Profile: React.FC = () => {
  const theme = useTheme();
  const userData = getStoredUserData();

  if (!userData) {
    return (
      <Box sx={{ px: 1, pt: 1 }}>
        <Typography>No user data found</Typography>
      </Box>
    );
  }

  const { user, games } = userData;

  const InfoRow = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value: string | React.ReactNode;
  }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
      <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value || '-'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ px: 1, pt: 1 }}>
      <PageHeader title="My Profile" subtitle="View your account information" />

      <Grid container spacing={2}>
        {/* Profile Header */}
        <Grid size={{ xs: 6 }}>
          <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
              <Avatar
                src={user.userProfileImage || undefined}
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: theme.palette.primary.main,
                  fontSize: 40,
                }}
              >
                {user.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {user.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {user.email}
                </Typography>
                <Chip
                  icon={<Verified />}
                  label={user.isGoogleLoggedIn ? 'Google Account' : 'Standard Account'}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* User Details */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Account Information
            </Typography>

            <InfoRow icon={<Person />} label="User ID" value={`#${user.id}`} />
            <InfoRow icon={<Email />} label="Email Address" value={user.email} />
            <InfoRow icon={<Badge />} label="Account Type" value={user.isGoogleLoggedIn ? 'Google SSO' : 'Standard'} />
          </Paper>
        </Grid>

        {/* Games Information */}
        <Grid size={{ xs: 6 }}>
          {games && games.length > 0 && (
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Associated Games
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {games.map((game) => (
                  <Box
                    key={game.game_id}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    {game.image && <Avatar src={game.image} variant="rounded" sx={{ width: 50, height: 50 }} />}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {game.game_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {game.internal_name} • {game.game_type}
                      </Typography>
                    </Box>
                    <Chip
                      label={game.status ? 'Active' : 'Inactive'}
                      color={game.status ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
